import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import CustomButton from "components/common/Button";
import { AlertType } from "components/common/Snackbar";
import { showSnackbar } from "store/actions/Utility";
import { retryBillingInvoiceCharge } from "services/billing";

import { BsCharge, formatMoney, summarizeFailureReason } from "./types";

/**
 * [PRD-3443] Manually retry a failed charge — reuses billing-service's existing
 * dunning-collection path (AdminService.collectInvoice), the same mechanism
 * dunning uses internally when it is running.
 */
export default function RetryChargeModal({
  charge,
  onClose,
  onDone,
}: {
  charge: BsCharge;
  onClose: () => void;
  onDone: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await retryBillingInvoiceCharge(charge.invoiceId);
      onDone();
      onClose();
      // The retry call itself succeeded (no 409/422 refusal), but the new
      // attempt's own outcome can still be a failure (e.g. the same card
      // declines again) — that's a soft failure, not an exception, so it
      // needs its own error-styled message rather than a success one.
      if (result?.chargeStatus === "failed") {
        // Short, readable reason; the full gateway text is in the Failure column.
        const reason = summarizeFailureReason(
          typeof result?.failureReason === "string" ? result.failureReason : null
        );
        dispatch(showSnackbar({ type: AlertType.error, message: `Retry failed. ${reason}`, dispatch }));
      } else if (result?.chargeStatus === "pending") {
        dispatch(
          showSnackbar({
            type: AlertType.success,
            message: "Charge retry initiated — payment is processing.",
            dispatch,
          })
        );
      } else {
        dispatch(
          showSnackbar({
            type: AlertType.success,
            message: "Charge retried successfully — payment succeeded.",
            dispatch,
          })
        );
      }
    } catch (err) {
      const serverMessage = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      const message =
        typeof serverMessage === "string" && serverMessage.trim()
          ? serverMessage
          : "Failed to retry this charge. Please try again.";
      setSubmitting(false);
      onClose();
      dispatch(showSnackbar({ type: AlertType.error, message, dispatch }));
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div className="flex justify-between items-center">
          <p className="heading-h4-semibold color-text-heading-primary">Retry charge</p>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-1">
          <p className="para-body-m-regular color-text-body-secondary">
            Retry {formatMoney(charge.amountCents, charge.currency)} for {charge.customerName || charge.customerId}?
            This will attempt to collect payment again using the customer&apos;s current default payment method.
          </p>
          {charge.failureReason && (
            <p className="para-body-s-regular color-text-body-secondary">
              Previous failure: {charge.failureReason}
            </p>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <CustomButton label="Cancel" onClick={onClose} />
        <CustomButton
          label={submitting ? "Retrying…" : "Retry charge"}
          onClick={handleConfirm}
          isDisabled={submitting}
        />
      </DialogActions>
    </Dialog>
  );
}
